<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { SIDEBAR_CONTEXT_KEY } from "@core/layouts/sidebar-context"
import type { SidebarMenuItem } from "@core/layouts/sidebars/sidebar-menu.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            header: {
                fullName: "{firstname} {lastname}",
            },
            menu: {
                settings: "Paramètres du compte",
                company: "Espace entreprise",
                companyGeneralInfo: "Informations générales",
                companySubscriptions: "Abonnements et suivis",
                companyMembers: "Membres",
                companyInvites: "Invités",
                companyInvoices: "Factures",
                companyProfile: "Profils d'utilisation",
                alarm: "Paramètres des alarmes",
                consent: "Page de consentement",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const sidebarContext = inject(SIDEBAR_CONTEXT_KEY)
const collapsed = computed(() => sidebarContext?.collapsed?.value ?? false)

// ================================
// COMPUTED - INFORMATIONS UTILISATEUR
// ================================

/**
 * URL de l'image de profil de l'utilisateur
 */
const getUserImage = computed(() => authStore.img)

/**
 * Initiales de l'utilisateur
 */
const getUserInitials = computed(() => {
    const first = authStore.firstname?.substring(0, 1) || ""
    const last = authStore.lastname?.substring(0, 1) || ""
    return `${first}${last}`.toUpperCase()
})

/**
 * Nom complet de l'utilisateur
 */
const getUserFullName = computed(() => {
    return t("header.fullName", {
        firstname: authStore.firstname,
        lastname: authStore.lastname,
    })
})

// ================================
// CONFIG MENU UTILISATEUR
// ================================

/** Chemin de la page entreprise (Espace entreprise). */
const COMPANY_PAGE_PATH = "/user/entreprise"

/**
 * Sous-onglets Espace entreprise, selon droits (aligné sur legacy user/index.vue).
 */
const getCompanySubItems = computed<SidebarMenuItem[]>(() => {
    const subItems: SidebarMenuItem[] = [
        {
            key: "generalInfo",
            label: t("menu.companyGeneralInfo"),
            route: { path: COMPANY_PAGE_PATH, hash: "#generalInfo" },
        },
    ]
    if (authStore.isEmployeWriteOrHigher) {
        subItems.push(
            {
                key: "subscriptions",
                label: t("menu.companySubscriptions"),
                route: { path: COMPANY_PAGE_PATH, hash: "#subscriptions" },
            },
            {
                key: "members",
                label: t("menu.companyMembers"),
                route: { path: COMPANY_PAGE_PATH, hash: "#members" },
            }
        )
        // « Invités » : la section CompanyClients de PageCompany n'est rendue
        // que pour un client Homeys (UC1) — cf. getShouldDisplayClients =
        // isClientHomeys && isEmployeWriteOrHigher. On masque donc l'entrée pour
        // les UC2 (UC2Pro inclus), sinon le lien pointe vers une section jamais
        // rendue (DEV-16283).
        if (authStore.isClientHomeys) {
            subItems.push({
                key: "invites",
                label: t("menu.companyInvites"),
                route: { path: COMPANY_PAGE_PATH, hash: "#invites" },
            })
        }
        if (authStore.role === "owner" && authStore.subscription?.plan) {
            subItems.push({
                key: "invoices",
                label: t("menu.companyInvoices"),
                route: { path: COMPANY_PAGE_PATH, hash: "#invoices" },
            })
        }
        if (authStore.isClientHomeys) {
            subItems.push({
                key: "profile",
                label: t("menu.companyProfile"),
                route: { path: COMPANY_PAGE_PATH, hash: "#profile" },
            })
        }
    }
    return subItems
})

const menuModel = computed<SidebarMenuItem[]>(() => {
    const items: SidebarMenuItem[] = [
        {
            key: "settings",
            label: t("menu.settings"),
            icon: "pi pi-cog",
            route: { path: "/user/settings" },
        },
    ]
    if (authStore.isClientHomeys || authStore.isUc2Pro) {
        items.push({
            key: "company",
            label: t("menu.company"),
            icon: "pi pi-building",
            route: { path: COMPANY_PAGE_PATH },
            items: getCompanySubItems.value,
        })
    }
    // « Paramètres des alarmes » : réservé aux clients Homeys (UC1). Masqué pour
    // les UC2 (particulier + UC2Pro), sans usage de cette page (décision produit
    // DEV-16283), et réservé à l'offre Energy Manager comme toutes les pages
    // d'alarmes (DEV-2252, « pas d'alarmes si pas de suivi »).
    if (authStore.isClientHomeys && authStore.hasOfferEnergyManager) {
        items.push({
            key: "alarm",
            label: t("menu.alarm"),
            icon: "pi pi-bell",
            route: { path: "/user/alarm" },
        })
    }
    // « Page de consentement » : conservée pour tous, y compris UC2 titulaire
    // (signature / suivi de ses propres consentements).
    items.push({
        key: "consent",
        label: t("menu.consent"),
        icon: "pi pi-file-edit",
        route: { path: "/consentements" },
    })
    return items
})
</script>

<template>
    <BaseSidebar :model="menuModel">
        <template #top>
            <!-- ================================ -->
            <!-- HEADER UTILISATEUR - MODE COLLAPSED -->
            <!-- ================================ -->
            <div
                v-if="collapsed"
                class="flex items-center justify-center px-2 pb-2 mb-2 border-b border-surface-200"
            >
                <div
                    class="w-10 h-10 rounded-full flex items-center justify-center border-2"
                    :class="
                        getUserImage
                            ? 'bg-transparent border-none'
                            : 'bg-primary-50 border-primary-200'
                    "
                >
                    <span
                        v-if="!getUserImage"
                        class="text-sm font-semibold uppercase select-none text-primary-600"
                    >
                        {{ getUserInitials }}
                    </span>
                    <img
                        v-else
                        :src="getUserImage"
                        :alt="getUserFullName"
                        class="w-full h-full rounded-full object-cover"
                    />
                </div>
            </div>

            <!-- ================================ -->
            <!-- HEADER UTILISATEUR - MODE EXPANDED -->
            <!-- ================================ -->
            <div
                v-else
                class="flex items-center gap-4 px-2 pb-2 mb-2 border-b border-surface-200"
            >
                <div
                    class="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                    :class="
                        getUserImage
                            ? 'bg-transparent border-none'
                            : 'bg-primary-50 border-primary-200'
                    "
                >
                    <span
                        v-if="!getUserImage"
                        class="text-sm font-semibold uppercase select-none text-primary-600"
                    >
                        {{ getUserInitials }}
                    </span>
                    <img
                        v-else
                        :src="getUserImage"
                        :alt="getUserFullName"
                        class="w-full h-full rounded-full object-cover"
                    />
                </div>
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                    <h2
                        class="text-base font-semibold text-surface-900 overflow-hidden text-ellipsis whitespace-nowrap m-0"
                        :title="getUserFullName"
                    >
                        {{ getUserFullName }}
                    </h2>
                </div>
            </div>
        </template>
    </BaseSidebar>
</template>
